import React, {useEffect, useRef, useState} from 'react';
import {Loader} from '@googlemaps/js-api-loader';
import {logError} from '../services/logger.service';
import {parsePlaceResult, ParsedAddressFields} from './AddressAutocompleteField';

/*
 * Google's address suggestions under a street field, as Android's
 * AddressAutocompleteField: typing lists matches, and picking one hands back
 * the address split into street, city, state and zip, with its point. A saved
 * street never searches on its own, only what is typed. The field and the
 * list are drawn by the page (the client card, the Account page's forms).
 */

export interface Suggestion {label: string; placeId: string}
export interface PickedAddress extends ParsedAddressFields {lat?: number; lng?: number}

const MAX_SUGGESTIONS = 8;
const SUGGEST_DEBOUNCE_MS = 280;

let placesLibraries: Promise<{autocomplete: google.maps.places.AutocompleteService; details: google.maps.places.PlacesService}> | undefined;

/** The Places services, loaded once for the page (the same loader options as every other map on the site). */
const loadPlaces = () => {
  placesLibraries ??= (async () => {
    const loader = new Loader({apiKey: 'AIzaSyBLw50vYSozTEviDIpu94K1KuwM4X_hDUM', version: 'beta'});
    const [places, maps] = await Promise.all([loader.importLibrary('places'), loader.importLibrary('maps')]);
    // PlacesService needs a map to hang off; this one is never shown.
    const map = new (maps as google.maps.MapsLibrary).Map(document.createElement('div'), {});
    return {
      autocomplete: new (places as google.maps.PlacesLibrary).AutocompleteService(),
      details: new (places as google.maps.PlacesLibrary).PlacesService(map),
    };
  })();
  placesLibraries.catch(() => { placesLibraries = undefined; });
  return placesLibraries;
};

const stripCountry = (value: string) => value.replace(/, (USA|United States)$/, '');

/**
 * The suggestions for what is typed into a street field. `search` with each
 * typed value; `onBlur` and `onKeyDown` go on the element around the field and
 * its list (arrows move, Enter picks, Escape closes).
 */
export const useStreetSuggestions = (onPicked: (address: PickedAddress) => void) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState<string>();
  const generation = useRef(0);

  useEffect(() => {
    const gen = ++generation.current;
    if (query === undefined || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = window.setTimeout(() => {
      loadPlaces()
        .then(({autocomplete}) => autocomplete.getPlacePredictions({
          input: query, componentRestrictions: {country: 'us'}, locationBias: 'IP_BIAS', region: 'US',
        }))
        .then(response => {
          if (gen !== generation.current) return;
          setSuggestions(response.predictions.slice(0, MAX_SUGGESTIONS)
            .map(p => ({label: stripCountry(p.description), placeId: p.place_id})));
          setActive(-1);
        })
        .catch(e => {
          logError('Error getting address suggestions', {address: query}, e as Error);
          if (gen === generation.current) setSuggestions([]);
        });
    }, SUGGEST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const close = () => {
    generation.current++;
    setQuery(undefined);
    setSuggestions([]);
  };

  const pick = (suggestion: Suggestion) => {
    close();
    void loadPlaces().then(({details}) => details.getDetails(
      {placeId: suggestion.placeId, fields: ['address_components', 'formatted_address', 'geometry']},
      (place, status) => {
        const found = status === google.maps.places.PlacesServiceStatus.OK && place ? place : undefined;
        const parsed = parsePlaceResult(found?.address_components, found?.formatted_address, suggestion.label);
        const point = found?.geometry?.location;
        // Five digits only (SQ-502): the ZIP+4 suffix is not wanted anywhere.
        onPicked({...parsed, postalCode: parsed.postalCode.slice(0, 5), lat: point?.lat(), lng: point?.lng()});
      },
    ));
  };

  const onBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => (a + (e.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return {suggestions, active, search: setQuery, pick, onBlur, onKeyDown};
};
